import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Mail, Loader2, KeyRound, ArrowLeft } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      toast({ title: "Something went wrong", description: err?.response?.data?.detail || "Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={sent ? "Check your email" : "Forgot your PIN?"}
      subtitle={
        sent
          ? "If an account exists for that email, we've sent a temporary 6-digit PIN."
          : "Enter your account email and we'll send you a temporary 6-digit PIN."
      }
      footer={
        <Link to="/login" className="inline-flex items-center gap-1.5 font-bold text-[#FF4500] hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to log in
        </Link>
      }
    >
      {!sent ? (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 pl-10"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="h-12 w-full rounded-full bg-[#FF4500] text-base font-bold text-white hover:bg-[#e03d00]"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send temporary PIN"}
          </Button>
        </form>
      ) : (
        <div className="space-y-5">
          <div className="flex items-start gap-3 rounded-xl bg-orange-50 p-4">
            <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-[#FF4500]" />
            <p className="text-sm text-gray-700">
              Check your inbox (and spam folder) for{" "}
              <span className="font-semibold">{email}</span>. Use the temporary
              PIN to log in, then you'll be asked to set your own 6-digit PIN.
            </p>
          </div>

          <Button
            onClick={() => navigate("/login")}
            className="h-12 w-full rounded-full bg-[#FF4500] text-base font-bold text-white hover:bg-[#e03d00]"
          >
            Continue to log in
          </Button>
        </div>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
